import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Compass, Sparkles, ArrowRight, RefreshCw, Check } from "lucide-react";

const REASONS = [
  "Aku lebih tertarik ke bidang lain",
  "Cita-citaku berbeda dari hasil asesmen",
  "Aku ingin menggabungkan minat & bakatku",
  "Aku belum yakin dengan hasilnya",
  "Aku ingin pilihan yang lebih menantang",
];

export default function ReconcileCareer() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (r) => setSelected((s) => (s.includes(r) ? s.filter((x) => x !== r) : [...s, r]));

  const submit = async () => {
    if (selected.length === 0 && !note.trim()) {
      toast.error("Pilih minimal satu alasan atau tulis penjelasan singkat");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/assessment/reconcile", { reasons: selected, catatan: note.trim() || null });
      setResult(data);
    } catch {
      toast.error("Gagal memuat alternatif, coba lagi");
    }
    setLoading(false);
  };

  return (
    <div className="mb-10" data-testid="section-reconcile">
      <div className="clay-card p-7 bg-[#4A6B53]/5 border-[#4A6B53]/25">
        <div className="flex items-start gap-3">
          <span className="h-11 w-11 rounded-xl bg-[#4A6B53] text-white grid place-items-center shrink-0"><Compass className="h-5 w-5" /></span>
          <div className="flex-1">
            <h3 className="font-heading font-black text-xl text-[#1A1C1A]">Anjuran belum sesuai cita-citamu?</h3>
            <p className="text-sm text-[#5C5F5C] mt-1">Tidak apa-apa! Ceritakan kenapa, dan sistem akan menawarkan alternatif yang <b>tetap selaras dengan potensimu</b> tapi lebih dekat dengan keinginanmu.</p>
            {!open && !result && (
              <button onClick={() => setOpen(true)} data-testid="reconcile-open-btn"
                className="mt-4 px-5 py-2.5 rounded-full bg-[#4A6B53] text-white text-sm font-medium hover:opacity-90 transition-opacity inline-flex items-center gap-2">
                Sesuaikan dengan keinginanku <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {open && !result && (
          <div className="mt-5 border-t border-[#4A6B53]/15 pt-5">
            <p className="text-sm font-semibold text-[#1A1C1A] mb-2">Kenapa anjuran sistem belum cocok?</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {REASONS.map((r) => (
                <button key={r} onClick={() => toggle(r)} data-testid={`reconcile-reason-${REASONS.indexOf(r)}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors inline-flex items-center gap-1.5 ${
                    selected.includes(r) ? "bg-[#4A6B53] text-white border-[#4A6B53]" : "border-[#E5E0D8] text-[#5C5F5C] hover:border-[#4A6B53]"}`}>
                  {selected.includes(r) && <Check className="h-3.5 w-3.5" />} {r}
                </button>
              ))}
            </div>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="reconcile-note-input"
              rows={3} placeholder="Tulis keinginan / cita-citamu lebih detail (opsional)…"
              className="w-full px-4 py-3 rounded-xl border border-[#E5E0D8] focus:border-[#4A6B53] outline-none text-[#1A1C1A] text-sm mb-4" />
            <div className="flex items-center gap-3">
              <button onClick={submit} disabled={loading} data-testid="reconcile-submit-btn"
                className="px-6 py-2.5 rounded-full bg-[#4A6B53] text-white text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2 hover:opacity-90 transition-opacity">
                {loading ? "Menyusun alternatif…" : "Lihat Alternatif"} <Sparkles className="h-4 w-4" />
              </button>
              <button onClick={() => setOpen(false)} className="text-sm text-[#5C5F5C]">Batal</button>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-5 border-t border-[#4A6B53]/15 pt-5" data-testid="reconcile-result">
            {result.pengakuan && <p className="text-[#1A1C1A] leading-relaxed mb-4">{result.pengakuan}</p>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(result.alternatif || []).map((a, i) => (
                <div key={i} className="clay-card p-5 bg-white" data-testid={`reconcile-alt-${i}`}>
                  <h4 className="font-heading font-bold text-lg text-[#1A1C1A] mb-1">{a.bidang}</h4>
                  {a.prodi?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {a.prodi.map((p, j) => <span key={j} className="px-2.5 py-1 rounded-full bg-[#E25E3E]/10 text-[#E25E3E] text-xs font-medium">{p}</span>)}
                    </div>
                  )}
                  <p className="text-sm text-[#5C5F5C] mb-3">{a.kenapa_selaras}</p>
                  {a.langkah?.length > 0 && (
                    <ul className="space-y-1.5">
                      {a.langkah.map((l, j) => <li key={j} className="flex items-start gap-2 text-xs text-[#1A1C1A]"><span className="text-[#4A6B53] mt-0.5">→</span> {l}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            {result.catatan_penyeimbang && (
              <div className="mt-4 rounded-xl bg-[#F4B942]/10 border border-[#F4B942]/30 p-4">
                <p className="text-sm text-[#1A1C1A] leading-relaxed"><b>Catatan penyeimbang:</b> {result.catatan_penyeimbang}</p>
              </div>
            )}
            <button onClick={() => { setResult(null); setOpen(true); }} data-testid="reconcile-retry-btn"
              className="mt-4 text-sm text-[#4A6B53] font-medium inline-flex items-center gap-1.5">
              <RefreshCw className="h-4 w-4" /> Coba alasan lain
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
